import {
	MetricAggregator,
	ProductType,
	SavedSegmentEntityType,
} from '@graph/schemas'
import {
	Box,
	DateRangePreset,
	DEFAULT_TIME_PRESETS,
	presetStartDate,
	Stack,
} from '@highlight-run/ui/components'
import { IntegrationCta } from '@pages/LogsPage/IntegrationCta'
import LogsHistogram from '@pages/LogsPage/LogsHistogram/LogsHistogram'
import { LogsTable } from '@pages/LogsPage/LogsTable/LogsTable'
import { useGetLogs } from '@pages/LogsPage/useGetLogs'
import useLocalStorage from '@rehooks/local-storage'
import { useParams } from '@util/react-router/useParams'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useQueryParam } from 'use-query-params'

import {
	DEFAULT_COLUMN_SIZE,
	SerializedColumn,
} from '@/components/CustomColumnPopover'
import { SearchContext } from '@/components/Search/SearchContext'
import {
	TIME_FORMAT,
	TIME_MODE,
} from '@/components/Search/SearchForm/constants'
import {
	FixedRangePreset,
	PermalinkPreset,
	QueryParam,
	SearchForm,
} from '@/components/Search/SearchForm/SearchForm'
import { parseSearch } from '@/components/Search/utils'
import { useGetMetricsQuery } from '@/graph/generated/hooks'
import { useNumericProjectId } from '@/hooks/useProjectId'
import { useSearchTime } from '@/hooks/useSearchTime'
import { TIMESTAMP_KEY } from '@/pages/Graphing/components/Graph'
import LogsCount from '@/pages/LogsPage/LogsCount/LogsCount'
import { LogsOverageCard } from '@/pages/LogsPage/LogsOverageCard/LogsOverageCard'
import {
	DEFAULT_LOG_COLUMNS,
	HIGHLIGHT_STANDARD_COLUMNS,
} from '@/pages/LogsPage/LogsTable/CustomColumns/columns'
import analytics from '@/util/analytics'
import { exportLogs } from '@pages/LogsPage/utils'
import { LeftPanel } from '@/components/Search/LeftPanel'
import { ControlsBar } from '@/components/Search/LeftPanel/ControlsBar'
import { useLeftPanel } from '@/components/Search/LeftPanel/useLeftPanel'

const MIN_DATE = presetStartDate(DEFAULT_TIME_PRESETS[5])

const LogsPage = () => {
	const { log_cursor } = useParams<{
		log_cursor: string
	}>()

	const timeMode = log_cursor !== undefined ? 'permalink' : 'fixed-range'
	const presetDefault =
		timeMode === 'permalink' ? PermalinkPreset : FixedRangePreset

	return (
		<LogsPageInner
			logCursor={log_cursor}
			timeMode={timeMode}
			presetDefault={presetDefault}
		/>
	)
}

type Props = {
	timeMode: TIME_MODE
	logCursor: string | undefined
	presetDefault: DateRangePreset
}

const LogsPageInner = ({ timeMode, logCursor, presetDefault }: Props) => {
	const { project_id } = useParams<{
		project_id: string
	}>()
	const [query, setQuery] = useQueryParam('query', QueryParam)
	const queryParts = useMemo(() => {
		return parseSearch(query).queryParts
	}, [query])

	const { displayLeftPanel, setDisplayLeftPanel } = useLeftPanel({
		key: 'logs',
	})

	const [selectedColumns, setSelectedColumns] = useLocalStorage<
		SerializedColumn[]
	>(`highlight-logs-table-columns`, DEFAULT_LOG_COLUMNS)
	const [windowSize, setWindowSize] = useLocalStorage(
		'highlight-logs-window-size',
		window.innerWidth,
	)

	useEffect(() => {
		if (!!setSelectedColumns) {
			const handleResize = () => {
				setWindowSize(window.innerWidth)
			}

			window.addEventListener('resize', handleResize)

			return () => {
				window.removeEventListener('resize', handleResize)
			}
		}
	}, [setSelectedColumns, setWindowSize])

	useEffect(() => {
		if (!!setSelectedColumns) {
			const newSelectedColumns = selectedColumns.map((column) => ({
				...column,
				size:
					HIGHLIGHT_STANDARD_COLUMNS[column.id]?.size ??
					DEFAULT_COLUMN_SIZE,
			}))

			setSelectedColumns(newSelectedColumns)
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [windowSize])

	const searchTimeContext = useSearchTime({
		presets: DEFAULT_TIME_PRESETS,
		initialPreset: presetDefault,
	})

	const {
		logEdges,
		moreLogs,
		pollingExpired,
		clearMoreLogs,
		loading,
		error,
		loadingAfter,
		fetchMoreForward,
		refetch,
	} = useGetLogs({
		query,
		project_id,
		logCursor,
		startDate: searchTimeContext.startDate,
		endDate: searchTimeContext.endDate,
		disablePolling: !searchTimeContext.selectedPreset,
	})

	const fetchMoreWhenScrolled = React.useCallback(
		(containerRefElement?: HTMLDivElement | null) => {
			if (containerRefElement) {
				const { scrollHeight, scrollTop, clientHeight } =
					containerRefElement
				if (scrollHeight - scrollTop - clientHeight < 100) {
					fetchMoreForward()
				}
			}
		},
		[fetchMoreForward],
	)

	const { projectId } = useNumericProjectId()
	const { data: histogramData, loading: histogramLoading } =
		useGetMetricsQuery({
			variables: {
				product_type: ProductType.Logs,
				project_id: project_id!,
				params: {
					query,
					date_range: {
						start_date: moment(searchTimeContext.startDate).format(
							TIME_FORMAT,
						),
						end_date: moment(searchTimeContext.endDate).format(
							TIME_FORMAT,
						),
					},
				},
				group_by: 'level',
				bucket_by: TIMESTAMP_KEY,
				bucket_count: 90,
				expressions: [
					{ aggregator: MetricAggregator.Count, column: '' },
				],
			},
			skip: !projectId,
		})

	let totalCount = 0
	for (const b of histogramData?.metrics.buckets ?? []) {
		totalCount += b.metric_value ?? 0
	}

	useEffect(() => {
		analytics.page('Logs')
	}, [])

	return (
		<SearchContext initialQuery={query} onSubmit={setQuery} {...searchTimeContext}>
			<Helmet>
				<title>Logs</title>
			</Helmet>
			<Box
				background="n2"
				padding="8"
				flex="stretch"
				justifyContent="stretch"
				display="flex"
			>
				<Box
					background="white"
					borderRadius="6"
					flexDirection="column"
					display="flex"
					flexGrow={1}
					border="dividerWeak"
					height="full"
					overflow="hidden"
					shadow="medium"
				>
					<SearchForm
						startDate={searchTimeContext.startDate}
						endDate={searchTimeContext.endDate}
						productType={ProductType.Logs}
						savedSegmentType={SavedSegmentEntityType.Log}
						enableAIMode={false}
						aiSupportedSearch={false}
						hideDatePicker
						onDatesChange={() => {}}
						presets={[]}
						minDate={MIN_DATE}
						timeMode={timeMode}
					/>
					<Box
						display="flex"
						flexDirection="row"
						height="full"
						overflow="hidden"
					>
						<LeftPanel
							product={ProductType.Logs}
							displayLeftPanel={displayLeftPanel}
							startDate={searchTimeContext.startDate}
							endDate={searchTimeContext.endDate}
						/>
						<Stack gap="0" flexGrow={1}>
							<ControlsBar
								showControlsPanel={displayLeftPanel}
								setShowControlsPanel={setDisplayLeftPanel}
								startDate={searchTimeContext.startDate}
								endDate={searchTimeContext.endDate}
								onDatesChange={
									searchTimeContext.updateSearchTime
								}
								presets={DEFAULT_TIME_PRESETS}
								minDate={MIN_DATE}
								selectedPreset={
									searchTimeContext.selectedPreset
								}
								timeMode={timeMode}
							/>
							<LogsCount
								startDate={searchTimeContext.startDate}
								endDate={searchTimeContext.endDate}
								presetSelected={
									!!searchTimeContext.selectedPreset
								}
								totalCount={totalCount}
								loading={histogramLoading}
								onDownload={
									loading
										? undefined
										: () => exportLogs(logEdges)
								}
							/>
							<LogsHistogram
								startDate={searchTimeContext.startDate}
								endDate={searchTimeContext.endDate}
								onDatesChange={
									searchTimeContext.updateSearchTime
								}
								loading={histogramLoading}
								metrics={histogramData}
							/>
							<Box
								borderTop="dividerWeak"
								height="full"
								overflow="hidden"
							>
								<LogsOverageCard />
								<IntegrationCta />
								<LogsTable
									query={query}
									queryParts={queryParts}
									logEdges={logEdges}
									loading={loading}
									error={error}
									refetch={refetch}
									loadingAfter={loadingAfter}
									selectedCursor={logCursor}
									moreLogs={moreLogs}
									clearMoreLogs={clearMoreLogs}
									handleAdditionalLogsDateChange={
										searchTimeContext.rebaseSearchTime
									}
									fetchMoreWhenScrolled={
										fetchMoreWhenScrolled
									}
									selectedColumns={selectedColumns}
									setSelectedColumns={setSelectedColumns}
									pollingExpired={pollingExpired}
								/>
							</Box>
						</Stack>
					</Box>
				</Box>
			</Box>
		</SearchContext>
	)
}

export default LogsPage
